<script>
	const breakpoints = require('utilities/breakpoints');
	const optimizedResize = require('utilities/optimizedResize');
	const {
		fireEvent,
		events: { SHOW_RECOMMENDATIONS, RECOMMENDATION_PANEL_ACTION }
	} = require('utilities/analyticsUtilities');
	const { assetUrl } = require('utilities/urlUtilities');

	module.exports = {
		components: {
			'recommended-article-card': require('components/recommended-article-card.vue').default
		},
		props: {
			recommendations: {
				type: Array,
				required: true
			},
			articles: {
				type: Array,
				required: true
			},
			classCode: {
				type: String,
				required: true
			},
			classes: {
				type: Array,
				required: true
			},
			max: {
				type: Number,
				required: true
			},
			loading: {
				type: Boolean,
				default: false
			}
		},
		modelData: ['userContext'],
		data() {
			return {
				articlesPerRow: 3,
				showAllArticles: false,
				shown: new Set()
			};
		},
		computed: {
			visibleArticles() {
				const articleThreshold = this.showAllArticles ? this.max : this.articlesPerRow;

				return this.articles.slice(0, articleThreshold);
			},
			buttonText() {
				return this.showAllArticles ? 'Show less' : 'Show more';
			},
			bannerImageURL() {
				return assetUrl('/assets/dist/images/recommendation-panel-banner.svg');
			}
		},
		methods: {
			onClassSelection(event) {
				this.$emit('class-selection', event.target.value);
			},
			toggleShowAllArticles() {
				const showAllArticles = !this.showAllArticles;
				this.showAllArticles = showAllArticles;

				fireEvent(RECOMMENDATION_PANEL_ACTION, {
					key: showAllArticles ? 'show-more' : 'show-less'
				});
			},
			getArticlesPerRow() {
				let articlesPerRow = 3;

				if (breakpoints.isMediumScreen()) {
					articlesPerRow = 2;
				} else if (breakpoints.isSmallScreen()) {
					articlesPerRow = 1;
				}

				this.articlesPerRow = articlesPerRow;
			}
		},
		mounted() {
			this.getArticlesPerRow();

			optimizedResize.add(this.getArticlesPerRow);
		},
		watch: {
			visibleArticles: {
				handler(articles) {
					const shownCount = this.shown.size;
					const articleCount = articles.length;

					const newRecommendations = this.recommendations.slice(shownCount, articleCount).filter(recommendation => !this.shown.has(recommendation.pid));

					if (newRecommendations.length > 0) {
						newRecommendations.forEach(recommendation => {
							this.shown.add(recommendation.pid);
						});

						fireEvent(SHOW_RECOMMENDATIONS, {
							cc: this.classCode,
							all: this.showAllArticles,
							new: newRecommendations
						});
					}
				},
				immediate: true
			},
			recommendations() {
				this.shown.clear();
				this.showAllArticles = false;
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/media';

	.recommended-content-panel {
		header {
			margin: -2.5rem -0.5rem 2rem;
			padding: 2rem 1rem;
			position: relative;
			display: flex;
			justify-content: center;
			flex-direction: column;

			@include media(sm, max) {
				margin-left: -0.25rem;
				margin-right: -0.25rem;
			}

			@include media(med) {
				min-height: 7rem;
				margin-left: -1rem;
				margin-right: -1rem;
			}

			@include media(lg) {
				min-height: 9rem;
			}
		}

		.banner-image {
			position: absolute;
			top: 0;
			left: 0;
			object-fit: cover;
			object-position: center center;
			width: 100%;
			height: 100%;
		}

		h2 {
			margin: 0;
			color: $c-blue-dark;
			position: relative;
		}

		.sub-heading {
			margin: 0;
			font-size: 0.875rem;
		}

		.class-select {
			position: absolute;
			bottom: -2.5rem;
			left: 2rem;
			width: 12.5rem;

			select {
				text-overflow: ellipsis;
			}
		}

		.loading-content,
		.empty-content {
			padding: 1rem;
			text-align: center;
			color: $c-slate-muted;
		}

		.articles {
			display: flex;
			flex-wrap: wrap;
			justify-content: center;

			@include media(med) {
				justify-content: space-between;
			}
		}

		.recommended-article-card {
			flex: 0 1 100%;

			@include media(med) {
				flex-basis: calc(50% - 2rem);
			}

			@include media(lg) {
				flex-basis: calc(33.333% - 3rem);
			}
		}
	}
</style>
<template>
	<section class="recommended-content-panel promotion-panel">
		<header>
			<img
				class="banner-image"
				:src="bannerImageURL"
				alt="" />
			<h2 class="has-sub-heading">Recommended For You</h2>
			<div
				class="input-select slim class-select print-hidden"
				v-if="classes.length > 1">
				<select
					:value="classCode"
					class="input"
					aria-label="Class code"
					@change="onClassSelection">
					<option
						v-for="c in classes"
						:key="c.classCode"
						:value="c.classCode">
						{{ c.name }}
					</option>
				</select>
			</div>
		</header>
		<div
			class="panel-content"
			aria-live="polite">
			<div
				v-if="loading"
				class="loading-content">
				Getting your recommendations...
			</div>
			<template v-else>
				<div class="articles">
					<recommended-article-card
						v-for="(article, index) in visibleArticles"
						:key="article.uuid"
						:article="article"
						:recommendation="recommendations[index]"
						@recommendation-click="$emit('recommendation-click')">
					</recommended-article-card>
				</div>
				<div
					v-if="articles.length === 0"
					class="empty-content">
					Unlock personalized recommendations! Once your students complete multiple passages and questions sets, we’ll provide tailored resources here to
					help them grow as readers.
				</div>
				<div class="text-centered">
					<button
						v-if="articles.length > articlesPerRow"
						type="button"
						ref="toggleShowAll"
						class="button -reverse toggle-show-all"
						@click="toggleShowAllArticles">
						{{ buttonText }}
					</button>
				</div>
			</template>
		</div>
	</section>
</template>
